import { useEffect, useState } from "react";

import {
  api,
  type Planner,
  type Regimen,
  type RegimenApplication,
  type RegimenBlock,
} from "@/api";
import { ErrorAlert } from "@/components/error-alert";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function when(b: RegimenBlock): string {
  const d = new Date(b.start_at);
  return `${d.toLocaleDateString([], { weekday: "short" })} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

/**
 * Apply a Regimen to days of the planner week: pick the Regimen and days,
 * see what will be created and what is skipped (already started, or
 * overlapping an existing block), then confirm to write to Google Calendar.
 */
export function ApplyRegimenDialog({
  planner,
  week,
  onClose,
  onApplied,
}: {
  planner: Planner;
  week: "current" | "next";
  onClose: () => void;
  onApplied: () => Promise<void>;
}) {
  const [regimens, setRegimens] = useState<Regimen[] | null>(null);
  const [regimenId, setRegimenId] = useState("");
  // Weekdays by default; weekends are opt-in. Days already past cannot be picked.
  const [days, setDays] = useState<Set<string>>(
    () =>
      new Set(
        planner.days
          .filter((d, i) => i < 5 && !d.is_past)
          .map((d) => d.date),
      ),
  );
  const [preview, setPreview] = useState<RegimenApplication | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const list = (await api.regimens()).regimens;
        setRegimens(list);
        if (list.length > 0) setRegimenId(list[0].id);
      } catch (err) {
        setError(messageOf(err, "could not load Regimens"));
      }
    })();
  }, []);

  function changed() {
    setPreview(null);
  }

  function toggle(date: string) {
    changed();
    setDays((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  }

  async function run(dryRun: boolean) {
    setBusy(true);
    setError(null);
    try {
      const result = await api.applyRegimen(regimenId, {
        week,
        days: [...days],
        dry_run: dryRun,
      });
      if (dryRun) {
        setPreview(result);
        setBusy(false);
      } else {
        await onApplied();
      }
    } catch (err) {
      setError(messageOf(err, "could not apply the Regimen"));
      setBusy(false);
    }
  }

  const ready = regimenId !== "" && days.size > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="apply-regimen-title"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xl">
        <div>
          <h2 id="apply-regimen-title" className="text-lg font-semibold">
            Apply a Regimen
          </h2>
          <p className="text-sm text-muted-foreground">
            Fills the days you tick with the Regimen&apos;s events. Blocks
            that already started or overlap something are skipped.
          </p>
        </div>

        {regimens && regimens.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No Regimens yet. Create one on the Regimens page first.
          </p>
        ) : (
          <>
            <Select
              value={regimenId}
              onValueChange={(v) => {
                changed();
                setRegimenId(v);
              }}
            >
              <SelectTrigger aria-label="Regimen">
                <SelectValue placeholder="Pick a Regimen" />
              </SelectTrigger>
              <SelectContent>
                {(regimens ?? []).map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <fieldset className="flex flex-wrap gap-2">
              <legend className="sr-only">Days</legend>
              {planner.days.map((d) => (
                <label
                  key={d.date}
                  className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-sm has-[:disabled]:opacity-50"
                >
                  <input
                    type="checkbox"
                    checked={days.has(d.date)}
                    disabled={d.is_past}
                    onChange={() => toggle(d.date)}
                  />
                  {new Date(`${d.date}T00:00`).toLocaleDateString([], {
                    weekday: "short",
                  })}
                </label>
              ))}
            </fieldset>

            {preview && (
              <div className="space-y-2 text-sm">
                <p className="font-medium">
                  {preview.created.length} to create
                  {preview.skipped.length > 0 &&
                    `, ${preview.skipped.length} skipped`}
                </p>
                <ul className="max-h-48 space-y-0.5 overflow-y-auto">
                  {preview.created.map((b) => (
                    <li key={b.start_at + b.title} className="flex gap-2">
                      <span className="tabular-nums text-muted-foreground">
                        {when(b)}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{b.title}</span>
                    </li>
                  ))}
                  {preview.skipped.map((b) => (
                    <li
                      key={b.start_at + b.title}
                      className="flex gap-2 text-muted-foreground"
                    >
                      <span className="tabular-nums">{when(b)}</span>
                      <span className="min-w-0 flex-1 truncate">
                        {b.title} · {b.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {error && <ErrorAlert message={error} />}
        <div className="flex flex-wrap gap-2">
          {preview ? (
            <Button
              onClick={() => void run(false)}
              disabled={busy || preview.created.length === 0}
            >
              Create {preview.created.length} blocks
            </Button>
          ) : (
            <Button onClick={() => void run(true)} disabled={busy || !ready}>
              Preview
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
